import { Icon } from "@iconify/react/dist/iconify.js";
import type { FC } from "react";
import de from "$/modules/i18n/asset/messages/de.json";
import en from "$/modules/i18n/asset/messages/en.json";

type ErrorFallbackProps = {
	/** The thrown error, when rendered as an error boundary. */
	error?: unknown;
	/** Router/boundary-provided retry. Falls back to a full reload. */
	reset?: () => void;
	variant?: "error" | "notFound";
};

/**
 * Provider-independent crash screen.
 *
 * Deliberately built from plain elements + Tailwind (no UI-library components,
 * no i18n hooks) so it renders even when the failure is in a provider or the
 * router itself — the whole point is that this never throws.
 */
export const ErrorFallback: FC<ErrorFallbackProps> = ({
	error,
	reset,
	variant = "error",
}) => {
	const isNotFound = variant === "notFound";
	const pathLocale =
		typeof window !== "undefined"
			? window.location.pathname.split("/").filter(Boolean)[0]
			: undefined;
	const localeFromDocument =
		globalThis.document?.documentElement.lang.toLowerCase();
	const locale =
		pathLocale === "de" || pathLocale === "en"
			? pathLocale
			: localeFromDocument?.startsWith("de")
				? "de"
				: "en";
	const copy = (locale === "de" ? de : en).common;

	const title = isNotFound ? copy.notFoundTitle : copy.errorTitle;
	const description = isNotFound
		? copy.notFoundDescription
		: copy.errorDescription;

	const errorMessage =
		error instanceof Error
			? error.message
			: typeof error === "string"
				? error
				: undefined;

	const handleRetry = () => {
		if (reset) {
			reset();
			return;
		}
		window.location.reload();
	};

	return (
		<div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background p-6 text-center text-foreground">
			<div className="flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary">
				<Icon
					icon={isNotFound ? "lucide:compass" : "lucide:triangle-alert"}
					className="size-8"
				/>
			</div>

			<div className="max-w-md space-y-2">
				<h1 className="font-display text-2xl font-bold sm:text-3xl">{title}</h1>
				<p className="text-foreground-500">{description}</p>
			</div>

			{errorMessage && !isNotFound && (
				<details className="max-w-md text-left">
					<summary className="cursor-pointer text-sm text-foreground-400">
						{copy.showDetails}
					</summary>
					<pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-default-100 p-3 text-xs text-foreground-600">
						{errorMessage}
					</pre>
				</details>
			)}

			<div className="flex flex-col gap-3 sm:flex-row">
				{!isNotFound && (
					<button
						type="button"
						onClick={handleRetry}
						className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
					>
						<Icon icon="lucide:refresh-cw" className="size-4" />
						{copy.tryAgain}
					</button>
				)}
				<a
					href="/"
					className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-default-200 px-6 py-2.5 font-semibold transition-colors hover:bg-default-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
				>
					<Icon icon="lucide:home" className="size-4" />
					{copy.home}
				</a>
			</div>
		</div>
	);
};
